import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader } from 'lucide-react';
import { courierApi } from '@/services/courierApi';
import { Courier, CourierFormData } from '@/types/courier';
import { vendorApi } from '@/services/vendorApi';
import { useAuthStore } from '@/stores/authStore';
import { db } from '@/services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import toast from 'react-hot-toast';

export const CourierDetail: React.FC = () => {
    const navigate = useNavigate();
    const { courierId } = useParams();
    const isNew = !courierId || courierId === 'create';
    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
    const isVendor = user?.role === 'vendor';

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [courier, setCourier] = useState<Partial<Courier> | null>(null);
    const [formData, setFormData] = useState<Partial<CourierFormData>>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        vendorId: '',
        status: 'active',
        servicingAreas: [],
    });
    const [vendors, setVendors] = useState<any[]>([]);
    const [currentVendorId, setCurrentVendorId] = useState<string | null>(null);
    const [currentVendorName, setCurrentVendorName] = useState('');

    useEffect(() => {
        void initializePage();
    }, [courierId, user?.uid, user?.role]);

    const initializePage = async () => {
        if (isNew) {
            setLoading(true);
            setCourier(null);

            try {
                if (isAdmin) {
                    const response = await vendorApi.getAllVendors({
                        limit: 1000,
                        offset: 0,
                    });
                    setVendors(response.vendors || []);
                    setCurrentVendorId(null);
                    setCurrentVendorName('');
                    setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                        password: '',
                        vendorId: '',
                        status: 'active',
                        servicingAreas: [],
                    });
                } else if (isVendor && user?.uid) {
                    const vendorQuery = query(
                        collection(db, 'vendors'),
                        where('uid', '==', user.uid)
                    );
                    const vendorSnapshot = await getDocs(vendorQuery);

                    if (vendorSnapshot.empty) {
                        throw new Error('Vendor bilgisi bulunamadı');
                    }

                    const vendorDoc = vendorSnapshot.docs[0];
                    const vendorData = vendorDoc.data();
                    const vendorName = `${vendorData.firstName || ''} ${vendorData.lastName || ''}`.trim();

                    setCurrentVendorId(vendorDoc.id);
                    setCurrentVendorName(vendorName || vendorData.companyName || 'Vendor');
                    setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                        password: '',
                        vendorId: vendorDoc.id,
                        status: 'active',
                        servicingAreas: [],
                    });
                }
            } catch (error: any) {
                console.error('Kurye oluşturma ekranı hazırlanamadı:', error);
                toast.error(error.message || 'Form hazırlanamadı');
            } finally {
                setLoading(false);
            }

            return;
        }

        if (isAdmin) {
            const response = await vendorApi.getAllVendors({
                limit: 1000,
                offset: 0,
            });
            setVendors(response.vendors || []);
        }

        await loadCourier();
    };

    const loadCourier = async () => {
        if (!courierId) return;
        try {
            const data = await courierApi.getCourier(courierId);
            setCourier(data);
            setCurrentVendorId(data.vendorId || null);
            setCurrentVendorName(data.vendor?.name || '');
            setFormData({
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phone: data.phone,
                vendorId: data.vendorId || '',
                status: data.status,
                servicingAreas: data.servicingAreas || [],
            });
        } catch (error) {
            console.error('Kurye yüklenemedi:', error);
            toast.error('Kurye yüklenemedi');
            navigate('/admin/couriers');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;

        if (name === 'vendorId') {
            setCurrentVendorId(value || null);
            setFormData((prev) => ({
                ...prev,
                vendorId: value,
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (
            !formData.firstName?.trim() ||
            !formData.lastName?.trim() ||
            !formData.email?.trim() ||
            !formData.phone?.trim() ||
            (isNew && !formData.password?.trim())
        ) {
            toast.error('Lütfen tüm zorunlu alanları doldurunuz');
            return;
        }

        if (isNew && (formData.password?.length || 0) < 6) {
            toast.error('Şifre en az 6 karakter olmalı');
            return;
        }

        if (!isNew && formData.password && formData.password.trim().length > 0 && formData.password.trim().length < 6) {
            toast.error('Şifre en az 6 karakter olmalı');
            return;
        }

        if (isAdmin && !formData.vendorId) {
            toast.error('Lütfen bir vendor seçiniz');
            return;
        }

        if (isVendor && !currentVendorId) {
            toast.error('Vendor bilgisi çözümlenemedi');
            return;
        }

        setSaving(true);
        try {
            if (isNew) {
                const payload = {
                    ...formData,
                    vendorId: isVendor && currentVendorId ? currentVendorId : formData.vendorId || '',
                } as CourierFormData;

                const result = isVendor && currentVendorId
                    ? await courierApi.createVendorCourier(currentVendorId, payload)
                    : await courierApi.createCourier(payload);
                toast.success('Kurye oluşturuldu');
                navigate(`/admin/couriers/${result.id}`);
            } else {
                await courierApi.updateCourier(
                    courierId!,
                    formData as Partial<CourierFormData>
                );
                toast.success('Kurye güncellendi');
                loadCourier();
            }
        } catch (error: any) {
            console.error('Kaydetme hatası:', error);
            toast.error(error.message || 'Kaydetme başarısız');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader className="animate-spin text-blue-600" size={40} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/admin/couriers')}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition"
                >
                    <ArrowLeft size={24} />
                </button>
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                        {isNew ? 'Yeni Kurye' : `${formData.firstName} ${formData.lastName}`}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow space-y-4">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                        Temel Bilgiler
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Ad *
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName || ''}
                                onChange={handleChange}
                                placeholder="Adı"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Soyadı *
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName || ''}
                                onChange={handleChange}
                                placeholder="Soyadı"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                E-posta (Giriş Adresi) *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email || ''}
                                onChange={handleChange}
                                placeholder="ornek@example.com"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Telefon *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone || ''}
                                onChange={handleChange}
                                placeholder="5551234567"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {isNew ? 'Giriş Şifresi *' : 'Şifre Değiştir (İsteğe Bağlı)'}
                            </label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password || ''}
                                onChange={handleChange}
                                placeholder={isNew ? 'En az 6 karakterli şifre' : 'Değiştirmek istemiyorsanız boş bırakın'}
                                minLength={6}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {!isNew && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Kuryenin şifresini yenilemek için yeni bir şifre girip kaydedin.
                                </p>
                            )}
                        </div>

                        {isAdmin && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Vendor *
                                </label>
                                <select
                                    name="vendorId"
                                    value={currentVendorId || ''}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Vendor seçiniz</option>
                                    {vendors.map((vendor) => (
                                        <option key={vendor.id} value={vendor.id}>
                                            {vendor.firstName} {vendor.lastName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {isVendor && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Vendor
                                </label>
                                <input
                                    type="text"
                                    value={currentVendorName}
                                    disabled
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700/70 dark:text-white focus:outline-none disabled:opacity-100"
                                />
                            </div>
                        )}

                        {!isNew && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Durum
                                </label>
                                <select
                                    name="status"
                                    value={formData.status || 'active'}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="active">Aktif</option>
                                    <option value="inactive">İnaktif</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Stats (if existing courier) */}
                {!isNew && courier && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Toplam Teslimler
                            </p>
                            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                                {courier.totalDeliveries || 0}
                            </p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Rating</p>
                            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                                {(courier.rating || 0).toFixed(1)} ⭐
                            </p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Bağlı Vendor
                            </p>
                            <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                                {courier.vendorId ? 1 : 0}
                            </p>
                        </div>
                    </div>
                )}

                {/* Save Button */}
                <div className="flex gap-2">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                    >
                        {saving ? (
                            <Loader size={20} className="animate-spin" />
                        ) : (
                            <Save size={20} />
                        )}
                        {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/admin/couriers')}
                        className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition"
                    >
                        İptal
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CourierDetail;
